import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHotelProductList, getProductDetails, saveProductBasicInfo, saveHotelBasicInfo, getHotelBasicInfo } from '../services/productService';
import ProductSettingsModal from '../components/ProductSettingsModal';
import HotelSettingsModal from '../components/HotelSettingsModal';
import ProductCard from '../components/ProductCard';
import ProductTable from '../components/ProductTable';
import ProductInventoryGrid from '../components/ProductInventoryGrid';
import HotelNewProductPlaceModal from '../components/HotelNewProductPlaceModal';
import PriceStockModal from '../components/PriceStockModal';
import { PlusIcon, MagnifyingGlassIcon, MapPinIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { getUserInfo } from '../utils/tokenManager';
import { getCities, getManagers, getProductTypes, getHotelProductTypes, getCurrencyCodes } from '../services/apiService';

const HotelProducts = () => {
  const navigate = useNavigate();
  const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyAJZldkS8iQk8VD9vb4NXkHvshYk4B_Q70';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [isPriceStockOpen, setIsPriceStockOpen] = useState(false);
  const [priceStockTarget, setPriceStockTarget] = useState(null);
  const [isNewProductOpen, setIsNewProductOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState({});
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    country: '',
    city: '',
    productType: '호텔',
    productSubType: '',
    saleStatus: 'true',
  });

  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    cities: [],
    productTypes: [],
    productSubTypes: [],
  });

  // Fetch product list when component mounts
  useEffect(() => {
    fetchProducts();
  }, []);

  // Populate filter options based on product list
  useEffect(() => {
    if (products.length > 0) {
      const countries = [...new Set(products.map(p => p.국가).filter(Boolean))];
      const productTypes = [...new Set(products.map(p => p.상품종류).filter(Boolean))];
      
      setFilterOptions(prev => ({ ...prev, countries, productTypes }));
    }
  }, [products]);

  // Update city and product sub-type options based on selection
  useEffect(() => {
    if (filters.country) {
      const cities = [...new Set(products
        .filter(p => p.국가 === filters.country)
        .map(p => p.도시)
        .filter(Boolean))];
      setFilterOptions(prev => ({ ...prev, cities }));
    } else {
      setFilterOptions(prev => ({ ...prev, cities: [] }));
      if (filters.city !== '') setFilters(prev => ({...prev, city: ''}));
    }

    // Get all product sub-types (all products are already hotel products from API filter)
    const productSubTypes = [...new Set(products
      .map(p => p.상품타입)
      .filter(Boolean))];
    setFilterOptions(prev => ({ ...prev, productSubTypes }));
    if (!productSubTypes.includes(filters.productSubType)) {
      setFilters(prev => ({ ...prev, productSubType: '' }));
    }
  }, [filters.country, products]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Parse product creation datetime safely
  const getCreatedAt = (product) => {
    try {
      const raw = product?.생성일시 || product?.등록일시 || product?.createdAt || '';
      if (!raw) return 0;
      const str = String(raw).trim();
      const iso = str.includes('T') ? str : (str.length === 16 ? str.replace(' ', 'T') + ':00' : str.replace(' ', 'T'));
      const t = Date.parse(iso);
      return isNaN(t) ? 0 : t;
    } catch {
      return 0;
    }
  };
  
  // Function to fetch products from API
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        alert('로그인이 필요합니다. 다시 로그인해 주세요.');
        navigate('/login');
        return;
      }
      const productData = await getHotelProductList(companyNo, userNo);
      setProducts(productData);
      setError(null);
    } catch (err) {
      setError('상품 목록을 불러오는데 실패했습니다.');
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on search term and filters
  const filteredProducts = products.filter(product => {
    const searchMatch = !searchTerm ||
      product.상품명?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.도시?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.상품종류?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.상품타입?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.상품코드?.toLowerCase().includes(searchTerm.toLowerCase());

    const countryMatch = !filters.country || product.국가 === filters.country;
    const cityMatch = !filters.city || product.도시 === filters.city;
    // productTypeMatch removed - already filtered by 상품종류코드 in getHotelProductList
    const productSubTypeMatch = !filters.productSubType || product.상품타입 === filters.productSubType;
    const saleStatusMatch = filters.saleStatus === 'all' || (product.판매여부 ? 'true' : 'false') === filters.saleStatus;

    return searchMatch && countryMatch && cityMatch && productSubTypeMatch && saleStatusMatch;
  });

  // Group products by country and city
  const groupProductsByCountryAndCity = (products) => {
    const grouped = {};
    
    products.forEach(product => {
      const country = product.국가 || '기타';
      const city = product.도시 || '기타';
      
      if (!grouped[country]) {
        grouped[country] = {};
      }
      
      if (!grouped[country][city]) {
        grouped[country][city] = [];
      }
      
      grouped[country][city].push(product);
    });
    
    return grouped;
  };
  
  const groupedProducts = groupProductsByCountryAndCity(filteredProducts);

  // Summary counts for 현황 모달
  const summary = {
    total: filteredProducts.length,
    onSale: filteredProducts.filter(p => !!p.판매여부).length,
    offSale: filteredProducts.filter(p => !p.판매여부).length,
  };

  const handleSettingsClick = async (product) => {
    setModalLoading(true);
    try {
      const { companyNo } = getUserInfo() || {};
      const details = await getProductDetails(product.상품번호, companyNo);
      if (details) {
        // Merge original list product fields (kind info, etc.) to allow hotel/tour detection in modal
        setSelectedProduct({ ...product, ...details });
        setIsModalOpen(true);
      } else {
        setError('상품 상세 정보를 찾을 수 없습니다.');
      }
    } catch (err) {
      setError('상품 상세 정보를 불러오는데 실패했습니다.');
      console.error('Failed to fetch product details:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleOpenPriceStock = (product) => {
    setPriceStockTarget(product);
    setIsPriceStockOpen(true);
  };

  const handleClosePriceStock = () => {
    setIsPriceStockOpen(false);
    setPriceStockTarget(null);
  };

  const handleSavePriceStock = async ({ productNo, data }) => {
    try {
      await fetchProducts();
    } catch (e) {
      console.error('가격/재고 저장 실패:', e);
    }
  };

  const handleToggleSale = async (product, nextSale) => {
    try {
      setLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) { alert('로그인이 필요합니다.'); navigate('/login'); return; }
      const productNo = product.상품번호;

      const details = await getProductDetails(productNo, companyNo).catch(() => null);
      const original = details || {};

      const basicInfo = {
        ...original,
        상품번호: productNo,
        노출여부: !!nextSale,
      };

      const keywordsStr = Array.isArray(original.키워드)
        ? original.키워드.join(',')
        : String(original.키워드 || '');

      await saveProductBasicInfo({ companyNo, userNo, productNo, basicInfo, keywords: keywordsStr });
      await fetchProducts();
    } catch (err) {
      console.error('판매여부 변경 실패:', err);
      setError('판매여부 변경에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (updatedProduct) => {
    try {
      setModalLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) { alert('로그인이 필요합니다.'); navigate('/login'); return false; }
      const productNo = updatedProduct.상품번호 || selectedProduct?.상품번호;

      const isHotel = (
        String(updatedProduct?.상품종류 || selectedProduct?.상품종류 || '').includes('호텔') ||
        String(updatedProduct?.상품종류코드 || selectedProduct?.상품종류코드 || '') === '001001'
      );
// 호텔과 투어의 기본정보를 분리하여 로드
      let original = null;
      
      if (isHotel) {
        // 호텔: 호텔 전용 프로시저로 최신 정보 로드
        const hotelOriginal = await getHotelBasicInfo(productNo, companyNo).catch(() => null);
        original = {
          ...(selectedProduct || {}),
          ...(hotelOriginal || {}),
        };
} else {
        // 투어: 투어 전용 프로시저로 최신 정보 로드
        const tourOriginal = await getProductDetails(productNo, companyNo).catch(() => null);
        original = {
          ...(selectedProduct || {}),
          ...(tourOriginal || {}),
        };
}

      const [cities, managers, productTypes, currencyList] = await Promise.all([
        getCities().catch(() => []),
        getManagers().catch(() => []),
        (isHotel ? getHotelProductTypes() : getProductTypes()).catch(() => []),
        (isHotel ? getCurrencyCodes() : Promise.resolve([])).catch(() => []),
      ]);

      const findByName = (list, name) => Array.isArray(list) ? list.find(i => i.코드명 === name) : undefined;

      const keywordStr = (updatedProduct.키워드 || '').trim();
      const keywordArr = keywordStr
        ? keywordStr.split(',').map(k => k.trim()).filter(Boolean)
        : [];

      // 도시 매핑: 도시명으로 도시번호를 찾되, 못 찾으면 기존 도시번호 유지
      const cityName = updatedProduct.도시 ?? original.도시;
      const cityItem = findByName(cities, cityName);
      // 도시번호는 숫자형으로 변환 (API가 숫자를 요구하는 경우 대비)
      const cityNo = cityItem?.코드 ? Number(cityItem.코드) : (original.도시번호 ? Number(original.도시번호) : null);
const productTypeName = updatedProduct.상품타입 ?? original.상품타입;
      const productTypeItem = findByName(productTypes, productTypeName);
      const productTypeCode = productTypeItem?.코드 ?? original.상품타입코드;

      // Currency mapping (호텔 전용): 원가통화명으로 통화코드를 찾되, 못 찾으면 기존 통화코드 유지
      const currencyName = updatedProduct.원가통화 ?? original.원가통화;
      const currencyItem = Array.isArray(currencyList) ? currencyList.find(c => c.코드명 === currencyName) : undefined;
      let currencyCode = currencyItem?.코드 ?? original.원가통화코드 ?? original.통화코드;
      // 통화코드가 6자리가 되도록 앞에 0 패딩 (예: 58001 -> 058001)
      if (currencyCode && String(currencyCode).length < 6) {
        currencyCode = String(currencyCode).padStart(6, '0');
      }
const pdName = updatedProduct.상품담당 ?? original.상품담당;
      const rdName = updatedProduct.예약담당 ?? original.예약담당;
      const pdItem = findByName(managers, pdName);
      const rdItem = findByName(managers, rdName);
      const pdNo = pdItem?.코드 ?? original.상품담당번호;
      const rdNo = rdItem?.코드 ?? original.예약담당번호;

      // 통화코드 패딩 처리 (호텔이 아닌 경우도 처리)
      let finalCurrencyCode = isHotel ? currencyCode : (original.통화코드 ?? '');
      if (finalCurrencyCode && String(finalCurrencyCode).length < 6) {
        finalCurrencyCode = String(finalCurrencyCode).padStart(6, '0');
      }

      const basicInfo = {
        ...original,
        상품번호: productNo,
        상품명: updatedProduct.상품명 ?? original.상품명,
        도시번호: cityNo,
        도시: cityName,
        // 호텔만 카테고리와 도시를 통일, 투어는 도시만 사용
        ...(isHotel ? {
          카테고리: cityName,
          카테고리코드: cityNo,
        } : {
          카테고리: original.카테고리 ?? '',
          카테고리코드: original.카테고리코드 ?? '',
        }),
        상품타입: productTypeName,
        상품타입코드: productTypeCode,
        상품코드: updatedProduct.상품코드 ?? original.상품코드,
        상품코드룰사용: original.상품코드룰사용 ?? false,
        상품담당: pdName,
        상품담당번호: pdNo,
        예약담당: rdName,
        예약담당번호: rdNo,
        디폴트수배담당: (original.디폴트수배담당 ?? original.예약담당 ?? original.상품담당) || '예약담당',
        소요타입: original.소요타입 ?? original.소요시간타입 ?? original.소요단위 ?? '',
        소요: original.소요 ?? original.소요단위 ?? '',
        소요시간: updatedProduct.소요시간 ?? original.소요시간 ?? 0,
        통화코드: finalCurrencyCode,
        통화: isHotel ? (currencyName ?? original.통화 ?? '') : (original.통화 ?? ''),
        가이드타입: original.가이드타입 ?? '',
        가이드타입코드: original.가이드타입코드 ?? '',
        노출여부: typeof updatedProduct.노출여부 === 'boolean' ? updatedProduct.노출여부 : (original.노출여부 ?? false),
        키워드: keywordArr,
        상품뱃지: updatedProduct.상품뱃지 ?? original.상품뱃지,
        // 호텔 전용 확장 필드
        ...(isHotel ? {
          체크인시간: updatedProduct.체크인시간 ?? original.체크인시간,
          체크아웃시간: updatedProduct.체크아웃시간 ?? original.체크아웃시간,
          원가통화: currencyName ?? '',
          원가통화코드: currencyCode ?? '',
          내부상품명: updatedProduct.상품명 ?? original.상품명,
        } : {}),
      };

      // 호텔과 투어를 분리하여 저장
      if (isHotel) {
await saveHotelBasicInfo({ companyNo, userNo, productNo, basicInfo, keywords: keywordArr.join(','), web: true });
        
        // 호텔: 저장 후 호텔 전용 프로시저로 최신 정보 다시 로드
        const hotelRow = await getHotelBasicInfo(productNo, companyNo).catch(() => null);
        if (hotelRow) {
          setSelectedProduct(prev => ({ ...(prev || {}), ...hotelRow }));
        }
      } else {
await saveProductBasicInfo({ companyNo, userNo, productNo, basicInfo, keywords: keywordArr.join(',') });
        
        // 투어: 저장 후 투어 전용 프로시저로 최신 정보 다시 로드
        const refreshed = await getProductDetails(productNo, companyNo).catch(() => null);
        if (refreshed) {
          setSelectedProduct(refreshed);
        }
      }
      await fetchProducts();
      alert('성공적으로 저장되었습니다.');

      return true;
    } catch (err) {
      setError('저장에 실패했습니다. 다시 시도해주세요.');
      console.error('Failed to save product details:', err);

      return false;
    } finally {
      setModalLoading(false);
    }
  };

  const toggleAccordion = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (products.length > 0) {
      const initialExpandedState = {};
      const allGrouped = groupProductsByCountryAndCity(products);
      Object.entries(allGrouped).forEach(([country, cities]) => {
        initialExpandedState[country] = true;
        Object.keys(cities || {}).forEach((city) => {
          initialExpandedState[`${country}-${city}`] = true;
        });
      });
      setExpanded(initialExpandedState);
    }
  }, [products]);

  // 상품 종류 판별
  const isHotelProduct = selectedProduct && (
    String(selectedProduct.상품종류 || '').includes('호텔') ||
    String(selectedProduct.상품종류코드 || '') === '001001'
  );

  return (
    <div className="space-y-8">
      {/* Modals - 호텔은 HotelSettingsModal, 투어는 ProductSettingsModal 사용 */}
      {isModalOpen && isHotelProduct && (
        <HotelSettingsModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleSaveSettings}
          loading={modalLoading}
        />
      )}
      {isModalOpen && !isHotelProduct && (
        <ProductSettingsModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleSaveSettings}
          loading={modalLoading}
        />
      )}
      {isPriceStockOpen && (
        <PriceStockModal
          product={priceStockTarget}
          open={isPriceStockOpen}
          onClose={handleClosePriceStock}
          onSave={handleSavePriceStock}
        />
      )}
      {isNewProductOpen && (
        <HotelNewProductPlaceModal
          open={isNewProductOpen}
          onClose={() => setIsNewProductOpen(false)}
          googleApiKey={GOOGLE_PLACES_API_KEY}
          onNext={({ productNo, hotelName, supplierNo, userNo, placeNo, partnerNo }) => {
            setIsNewProductOpen(false);
            if (productNo) {
              navigate(`/detail-settings/${productNo}`);
            }
          }}
        />
      )}

      {/* Filter and Search Bar */}
      <div className="mb-6 sticky top-0 z-[31]">
        <div className="bg-white py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-3 md:gap-4 items-end">
            {/* Country Filter */}
            <div className="lg:col-span-2">
              <label htmlFor="country-filter" className="block text-xs font-medium text-gray-700 mb-1">국가</label>
              <select id="country-filter" name="country" value={filters.country} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-8 py-2 text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 rounded-lg bg-white">
                <option value="">전체</option>
                {filterOptions.countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* City Filter */}
            {filters.country && (
              <div className="lg:col-span-2">
                <label htmlFor="city-filter" className="block text-xs font-medium text-gray-700 mb-1">도시</label>
                <select id="city-filter" name="city" value={filters.city} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-8 py-2 text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 rounded-lg bg-white">
                  <option value="">전체</option>
                  {filterOptions.cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Product Sub-Type Filter */}
            <div className="lg:col-span-2">
              <label htmlFor="productSubType-filter" className="block text-xs font-medium text-gray-700 mb-1">상품 타입</label>
              <select id="productSubType-filter" name="productSubType" value={filters.productSubType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-8 py-2 text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 rounded-lg bg-white">
                <option value="">전체</option>
                {filterOptions.productSubTypes.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>

            {/* Sale Status Filter */}
            <div className="lg:col-span-2">
              <label htmlFor="saleStatus-filter" className="block text-xs font-medium text-gray-700 mb-1">판매 여부</label>
              <select id="saleStatus-filter" name="saleStatus" value={filters.saleStatus} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-8 py-2 text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 rounded-lg bg-white">
                <option value="all">전체</option>
                <option value="true">판매중</option>
                <option value="false">판매중지</option>
              </select>
            </div>

            {/* Search + Actions */}
            <div className={`sm:col-span-2 md:col-span-3 ${filters.country ? 'lg:col-span-4' : 'lg:col-span-6'} flex flex-col md:flex-row md:items-end md:flex-nowrap gap-2 md:justify-end`}>
              <div className="relative w-full md:max-w-[12rem] md:flex-[0_0_auto]">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="inline-search-input"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 text-sm"
                  placeholder="상품 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button type="button" onClick={() => setIsStatusModalOpen(true)} className="w-full md:w-auto inline-flex items-center justify-center px-3.5 py-2 whitespace-nowrap text-sm font-medium rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-colors">상품등록 현황</button>
              <button
                onClick={() => navigate('/quick-wizard')}
                className="hidden w-full md:w-auto inline-flex items-center justify-center px-3.5 py-2 whitespace-nowrap border border-gray-200 text-sm font-medium rounded-lg text-white bg-gray-700 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 transition-colors md:flex-[0_0_auto]"
                title="최소 정보로 빠르게 상품 등록"
              >
                스마트 상품 등록
              </button>
              <button
                onClick={() => setIsNewProductOpen(true)}
                className="w-full md:w-auto inline-flex items-center justify-center px-3.5 py-2 whitespace-nowrap text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 transition-colors"
              >
                <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                새 상품 등록
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600"></div>
            <p className="text-sm text-gray-500">상품 목록을 불러오는 중...</p>
          </div>
        </div>
      )}

      {isStatusModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsStatusModalOpen(false)} />
          <div className="relative z-10 w-[min(1400px,98vw)] max-h-[90vh] overflow-auto bg-white rounded-xl shadow-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">상품등록 현황</h2>
              <button type="button" className="px-2 py-1 text-sm rounded border bg-white hover:bg-gray-50" onClick={() => setIsStatusModalOpen(false)}>닫기</button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 border">전체 <strong className="ml-1">{summary.total}</strong></span>
              <span className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-gray-50 text-gray-700 border border-gray-200">판매중 <strong className="ml-1">{summary.onSale}</strong></span>
              <span className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-gray-50 text-gray-700 border border-gray-200">중지 <strong className="ml-1">{summary.offSale}</strong></span>
            </div>
            <ProductTable
              products={filteredProducts}
              onToggleSale={handleToggleSale}
              onOpenSettings={handleSettingsClick}
              onOpenOrders={(p) => {
                const productNo = p?.상품번호 || '';
                navigate(`/orders/${encodeURIComponent(productNo)}`, { state: { productName: p?.상품명 || '', productNo: p?.상품번호 || null, productCode: p?.상품코드 || null } });
              }}
              onOpenSalesCondition={(p) => {
                const productNo = p?.상품번호 || '';
                navigate(`/sales-condition/${encodeURIComponent(productNo)}`);
              }}
              onOpenPriceStock={(p) => navigate(`/price-stock/${p?.상품번호 || ''}`)}
              onOpenDetailSettings={(p) => navigate(`/detail-settings/${p?.상품번호 || ''}`)}
              onNameClick={(p) => {
                const productNo = p?.상품번호 || '';
                navigate(`/orders/${encodeURIComponent(productNo)}`, { state: { productName: p?.상품명 || '', productNo: p?.상품번호 || null, productCode: p?.상품코드 || null } });
              }}
              showOrdersButton={false}
              nameLinkNeutral
              enableColumnFilters
              dateOnly
              singleDateFilter
              disablePagination
              showModalExtraColumns
              actionsInName
              pageSize={20}
              onNoteSaved={(prod, note) => {
                setProducts((prev) => Array.isArray(prev) ? prev.map((p) => {
                  const idA = p?.상품번호 || p?.상품코드;
                  const idB = prod?.상품번호 || prod?.상품코드;
                  if (idA && idB && String(idA) === String(idB)) {
                    return { ...p, 상품비고: note };
                  }
                  return p;
                }) : prev);
              }}
            />
          </div>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div>
          {filteredProducts.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500">검색 결과가 없습니다.</p>
            </div>
          ) : (
            <ProductInventoryGrid
              rowData={filteredProducts}
              onToggleSale={handleToggleSale}
              onOpenBasicSettings={handleSettingsClick}
              onOpenSalesCondition={(p) => {
                const productNo = p?.상품번호 || '';
                navigate(`/sales-condition/${encodeURIComponent(productNo)}`);
              }}
              onOpenPriceStock={(p) => navigate(`/price-stock/${p?.상품번호 || ''}`)}
              onOpenDetailSettings={(p) => navigate(`/detail-settings/${p?.상품번호 || ''}`)}
              onOpenOrders={(p) => {
                const productNo = p?.상품번호 || '';
                navigate(`/orders/${encodeURIComponent(productNo)}`, { state: { productName: p?.상품명 || '', productNo: p?.상품번호 || null, productCode: p?.상품코드 || null } });
              }}
              onNoteSaved={(prod, note) => {
                setProducts((prev) => Array.isArray(prev) ? prev.map((p) => {
                  const idA = p?.상품번호 || p?.상품코드;
                  const idB = prod?.상품번호 || prod?.상품코드;
                  if (idA && idB && String(idA) === String(idB)) {
                    return { ...p, 상품비고: note };
                  }
                  return p;
                }) : prev);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default HotelProducts;
